/**
 * Asks for just the universal-profile details an action needs (mobile number,
 * date of birth, profile photo; Instagram as an optional extra), in place, then hands control back to the
 * flow that opened it. Saved details go to the user's profile, so they're
 * asked once. Opened through useProfileGate().
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '../../config/axios';
import { useAuth } from '../../context/AuthContext';
import { uploadToCloudinary } from '../../lib/uploads';
import { formatIndianMobile, isValidIndianMobile } from '../../lib/phone';
import { normaliseInstagram } from '../../lib/instagram';

const inputCls = 'w-full border-2 border-gray-100 focus:border-[#2B3A67] rounded-xl px-4 py-3 text-sm outline-none';
const labelCls = 'text-xs font-bold uppercase tracking-widest text-gray-400';

const TODAY = new Date().toISOString().slice(0, 10);

export default function CompleteDetailsSheet({ fields, required = fields, reason, onDone, onCancel }) {
  const { user, updateUser } = useAuth();
  const [mobile, setMobile] = useState(user?.mobile_number || '');
  const [dob, setDob] = useState(user?.date_of_birth ? String(user.date_of_birth).slice(0, 10) : '');
  const [photoUrl, setPhotoUrl] = useState(user?.profile_picture_url || '');
  const [instagram, setInstagram] = useState(user?.instagram_handle ? `@${user.instagram_handle}` : '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const show = (f) => fields.includes(f);
  const must = (f) => required.includes(f);

  const pickPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    setUploading(true);
    try {
      setPhotoUrl(await uploadToCloudinary(file, 'users/avatars'));
    } catch {
      toast.error('Photo upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (show('mobile_number') && (must('mobile_number') || mobile) && !isValidIndianMobile(mobile)) {
      toast.error('Enter a valid 10-digit mobile number'); return;
    }
    if (show('date_of_birth') && must('date_of_birth') && !dob) { toast.error('Enter your date of birth'); return; }
    if (show('profile_picture') && must('profile_picture') && !photoUrl) { toast.error('Add a profile photo'); return; }
    const handle = show('instagram_handle') ? normaliseInstagram(instagram) : undefined;
    if (handle === null) { toast.error('Enter a valid Instagram handle, e.g. @yourname'); return; }

    setSaving(true);
    try {
      let next = user;
      const patch = {};
      if (show('mobile_number') && mobile && mobile !== user?.mobile_number) patch.mobile_number = mobile;
      if (show('date_of_birth') && dob && dob !== String(user?.date_of_birth || '').slice(0, 10)) patch.date_of_birth = dob;
      if (handle !== undefined && handle !== (user?.instagram_handle || '')) patch.instagram_handle = handle;
      if (Object.keys(patch).length) next = (await axiosInstance.patch('/auth/profile', patch)).data;
      if (show('profile_picture') && photoUrl && photoUrl !== user?.profile_picture_url) {
        next = (await axiosInstance.patch('/auth/profile-picture', { profile_picture_url: photoUrl })).data;
      }
      updateUser(next);
      onDone(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1200] flex items-end sm:items-center justify-center" onClick={onCancel}
      data-testid="complete-details-sheet">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-safe space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-extrabold text-[#2B3A67]">
              {required.length ? 'A few details first' : 'Your details'}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {reason || 'We need these to complete your request.'} They're saved to your profile, so you'll only do this once.
            </p>
          </div>
          <button type="button" onClick={onCancel} className="p-1 text-gray-400" aria-label="Close">
            <i className="ph-bold ph-x text-lg" />
          </button>
        </div>

        {show('profile_picture') && (
          <div className="flex items-center gap-4">
            <label className="relative cursor-pointer">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-200">
                {photoUrl
                  ? <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                  : <i className="ph-bold ph-camera text-2xl text-gray-400" />}
                {uploading && <div className="absolute inset-0 bg-white/70 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2B3A67]" /></div>}
              </div>
              <input type="file" accept="image/*" className="hidden" disabled={uploading}
                onChange={(e) => pickPhoto(e.target.files?.[0])} data-testid="details-photo-input" />
            </label>
            <div>
              <p className={labelCls}>Profile photo{must('profile_picture') && <span className="text-red-500"> *</span>}</p>
              <p className="text-xs text-gray-500 mt-1">A clear photo of your face — shown to organisers and teams.</p>
            </div>
          </div>
        )}

        {show('mobile_number') && (
          <div>
            <label htmlFor="details-mobile" className={labelCls}>
              Mobile number{must('mobile_number') && <span className="text-red-500"> *</span>}
            </label>
            <input id="details-mobile" type="tel" inputMode="tel" className={`${inputCls} mt-2`} placeholder="+91 98765 43210"
              value={mobile} onChange={(e) => setMobile(formatIndianMobile(e.target.value))}
              data-testid="details-mobile-input" />
          </div>
        )}

        {show('date_of_birth') && (
          <div>
            <label htmlFor="details-dob" className={labelCls}>
              Date of birth{must('date_of_birth') && <span className="text-red-500"> *</span>}
            </label>
            <input id="details-dob" type="date" max={TODAY} className={`${inputCls} mt-2`}
              value={dob} onChange={(e) => setDob(e.target.value)} data-testid="details-dob-input" />
            <p className="text-[11px] text-gray-400 mt-1">Needed for age-restricted tournaments.</p>
          </div>
        )}

        {show('instagram_handle') && (
          <div>
            <label htmlFor="details-instagram" className={labelCls}>
              Instagram <span className="normal-case tracking-normal font-normal text-gray-400">(optional)</span>
            </label>
            <div className="relative mt-2">
              <i className="ph-bold ph-instagram-logo absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input id="details-instagram" className={`${inputCls} pl-10`} placeholder="@yourhandle or profile link"
                autoCapitalize="none" autoCorrect="off" value={instagram} onChange={(e) => setInstagram(e.target.value)}
                data-testid="details-instagram-input" />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              We may tag you in posts about events you play in and tell you about Backyard events and offers.
              Remove it any time from your profile. <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline">Privacy Policy</a>
            </p>
          </div>
        )}

        <button type="submit" disabled={saving || uploading}
          className="w-full py-3.5 rounded-2xl bg-[#2B3A67] text-white font-extrabold disabled:opacity-50"
          data-testid="details-save-btn">
          {saving ? 'Saving…' : 'Save and continue'}
        </button>
      </form>
    </div>
  );
}
